import React from 'react';
import { Box, Typography } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import NoteIcon from '@mui/icons-material/Note';
import { PintGlassEmptyIcon, PintGlassFullIcon, PintGlassHalfIcon, TastingListIcon } from '../icons/PintGlassIcons';

interface StatusBadgeProps {
  type: 'availability' | 'rating' | 'favourite' | 'notes' | 'tasting_list';
  availability?: 'available' | 'sold_out' | 'not_ready';
  availabilityStyle?: 'default' | 'glass';
  rating?: number;
  isActive?: boolean;
  hasContent?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'compact';
}

const getAvailabilityColor = (availability: string): string => {
  switch (availability) {
    case 'available':
      return '#4caf50';
    case 'sold_out':
      return '#f44336';
    case 'not_ready':
      return '#ffc107';
    default:
      return '#9e9e9e';
  }
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  type,
  availability,
  availabilityStyle = 'default',
  rating,
  isActive = false,
  hasContent = false,
  onClick,
  variant = 'default',
}) => {
  const isCompact = variant === 'compact';

  const sizePx = isCompact ? 28 : 30;
  const radiusPx = isCompact ? 10 : 12;

  const backgroundColour = 'color-mix(in srgb, var(--secondary-colour) 86%, black)';
  const hoverBackgroundColour = 'color-mix(in srgb, var(--secondary-colour) 78%, black)';

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: isCompact ? 0.35 : 0.5,
    padding: 0,
    borderRadius: `${radiusPx}px`,
    backgroundColor: backgroundColour,
    boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
    cursor: onClick ? 'pointer' : 'default',
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    justifyContent: 'center',
    transition: 'background-color 140ms ease, border-color 140ms ease, transform 90ms ease',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    ...(onClick
      ? {
          '&:hover': {
            backgroundColor: hoverBackgroundColour,
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }
      : {}),
    '&:focus-visible': {
      outline: '2px solid color-mix(in srgb, var(--primary-colour) 65%, transparent)',
      outlineOffset: '2px',
    },
  };

  if (type === 'availability' && availability) {
    const color = getAvailabilityColor(availability);

    const glassIcon = (() => {
      switch (availability) {
        case 'available':
          return PintGlassFullIcon;
        case 'not_ready':
          return PintGlassHalfIcon;
        case 'sold_out':
          return PintGlassEmptyIcon;
        default:
          return PintGlassEmptyIcon;
      }
    })();

    return (
      <Box
        className={`StatusBadge StatusBadge--${type}`}
        data-status-badge
        data-status-badge-type={type}
        data-status-badge-availability={availability}
        onClick={onClick}
        sx={{
          ...baseStyles,
          border: '1px solid var(--primary-colour)',
        }}
      >
        {availabilityStyle === 'glass' ? (
          React.createElement(glassIcon, { size: isCompact ? 18 : 18, colour: color })
        ) : (
          <Box
            sx={{
              width: isCompact ? '7px' : '6px',
              height: isCompact ? '7px' : '6px',
              borderRadius: '50%',
              backgroundColor: color,
            }}
          />
        )}
      </Box>
    );
  }

  if (type === 'rating' && rating !== undefined && rating > 0) {
    return (
      <Box
        className={`StatusBadge StatusBadge--${type}`}
        data-status-badge
        data-status-badge-type={type}
        sx={{
          ...baseStyles,
          border: '1px solid var(--primary-colour)',
          width: isCompact ? `${sizePx}px` : 'auto',
          minWidth: isCompact ? `${sizePx}px` : `${sizePx}px`,
          paddingLeft: isCompact ? 0 : '6px',
          paddingRight: isCompact ? 0 : '6px',
        }}
      >
        <Typography
          sx={{
            fontSize: isCompact ? '0.75rem' : '0.8rem',
            fontWeight: 700,
            color: 'var(--primary-colour)',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {rating.toFixed(1)}
        </Typography>
      </Box>
    );
  }

  if (type === 'tasting_list') {
    return (
      <Box
        className={`StatusBadge StatusBadge--${type}`}
        data-status-badge
        data-status-badge-type={type}
        onClick={onClick}
        sx={{
          ...baseStyles,
          border: '1px solid var(--primary-colour)',
        }}
      >
        <TastingListIcon size={isCompact ? 18 : 18} colour={isActive ? 'var(--primary-colour)' : 'rgba(255, 255, 255, 0.55)'} />
      </Box>
    );
  }

  if (type === 'favourite') {
    return (
      <Box
        className={`StatusBadge StatusBadge--${type}`}
        data-status-badge
        data-status-badge-type={type}
        onClick={onClick}
        sx={{
          ...baseStyles,
          border: '1px solid var(--primary-colour)',
          justifyContent: 'center',
        }}
      >
        {isActive ? (
          <FavoriteIcon sx={{ fontSize: isCompact ? '1.05rem' : '1rem', color: '#ff4040' }} />
        ) : (
          <FavoriteBorderIcon sx={{ fontSize: isCompact ? '1.05rem' : '1rem', color: '#ff4040' }} />
        )}
      </Box>
    );
  }

  if (type === 'notes') {
    return (
      <Box
        className={`StatusBadge StatusBadge--${type}`}
        data-status-badge
        data-status-badge-type={type}
        sx={{
          ...baseStyles,
          border: '1px solid var(--primary-colour)',
          justifyContent: 'center',
        }}
      >
        <NoteIcon
          sx={{
            fontSize: isCompact ? '1rem' : '0.9rem',
            color: hasContent ? '#2196f3' : 'rgba(255, 255, 255, 0.65)',
          }}
        />
      </Box>
    );
  }

  return null;
};

export default StatusBadge;
